import OpenAI from 'openai';
import type { QuizConfig, Question, LLMConfig, Difficulty, Era, QuestionType, Subject } from '../types';

const ERA_DESCRIPTIONS: Record<string, string> = {
  prehistoric: '선사시대(구석기, 신석기, 청동기)',
  gojoseon: '고조선(단군조선, 위만조선, 한사군 설치)',
  early_states: '원삼국시대(부여, 옥저, 동예, 삼한)',
  three_kingdoms: '삼국시대(고구려, 백제, 신라, 가야)',
  unified_silla: '남북국시대(통일신라, 발해)',
  goryeo: '고려시대(918-1392)',
  joseon_early: '조선전기(1392-1592, 태조~선조, 임진왜란 이전)',
  joseon_late: '조선후기(1592-1876, 임진왜란~강화도조약)',
  enlightenment: '개화기/대한제국(1876-1910, 개항~경술국치)',
  japanese_colonial: '일제강점기(1910-1945)',
  modern: '현대(1945~현재, 해방, 분단, 민주화)',
};

const MATH_TERM_DESCRIPTIONS: Record<string, string> = {
  elem6_1: '초등학교 6학년 1학기',
  elem6_2: '초등학교 6학년 2학기',
  mid1_1: '중학교 1학년 1학기',
  mid1_2: '중학교 1학년 2학기',
  mid2_1: '중학교 2학년 1학기',
  mid2_2: '중학교 2학년 2학기',
  mid3_1: '중학교 3학년 1학기',
  mid3_2: '중학교 3학년 2학기',
};

const TYPE_DESCRIPTIONS: Record<string, string> = {
  multiple_choice: '객관식(4개 보기)',
  short_answer: '주관식(단답형)',
  ox: 'OX퀴즈',
};

const DIFFICULTY_DESCRIPTIONS: Record<Difficulty, string> = {
  easy: '쉬움',
  medium: '중간',
  hard: '어려움',
};

type QuestionSpec = {
  era: Era;
  type: QuestionType;
  difficulty: Difficulty;
};

type BatchPromptMeta = {
  batchNumber: number;
  totalBatches: number;
  totalCount: number;
  startQuestionNumber: number;
};

type EraFocusRange =
  | { kind: 'year'; start: number; end: number }
  | { kind: 'phase'; phases: string[] };

const ERA_FOCUS_RANGES: Partial<Record<Era, EraFocusRange>> = {
  prehistoric: { kind: 'phase', phases: ['구석기', '신석기', '청동기'] },
  gojoseon: { kind: 'year', start: -2333, end: -108 },
  early_states: { kind: 'year', start: -108, end: 300 },
  three_kingdoms: { kind: 'year', start: 300, end: 676 },
  unified_silla: { kind: 'year', start: 676, end: 935 },
  goryeo: { kind: 'year', start: 918, end: 1392 },
  joseon_early: { kind: 'year', start: 1392, end: 1592 },
  joseon_late: { kind: 'year', start: 1592, end: 1876 },
  enlightenment: { kind: 'year', start: 1876, end: 1910 },
  japanese_colonial: { kind: 'year', start: 1910, end: 1945 },
  modern: { kind: 'year', start: 1945, end: 2025 },
};

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

function extractAnswerIndex(answer: string): number | null {
  const normalized = answer.trim().toUpperCase();
  const byNumber = normalized.match(/^[(\[]?([1-9])[\])]?$/);
  if (byNumber) return Number(byNumber[1]) - 1;

  const byLetter = normalized.match(/^[(\[]?([A-D])[\])]?$/);
  if (byLetter) return byLetter[1].charCodeAt(0) - 65;

  return null;
}

function shuffleArray<T>(items: T[]): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function randomizeMultipleChoiceOptions(questions: Question[]): Question[] {
  return questions.map((question) => {
    if (question.type !== 'multiple_choice' || !question.options || question.options.length < 2) {
      return question;
    }

    const options = [...question.options];
    const normalizedAnswer = normalizeText(question.answer);
    let correctIndex = options.findIndex((opt) => normalizeText(opt) === normalizedAnswer);

    if (correctIndex < 0) {
      const parsedIndex = extractAnswerIndex(question.answer);
      if (parsedIndex !== null && parsedIndex >= 0 && parsedIndex < options.length) {
        correctIndex = parsedIndex;
      }
    }

    if (correctIndex < 0) return question;

    const originalCorrectOption = options[correctIndex];
    const shuffledOptions = shuffleArray(options);

    return {
      ...question,
      options: shuffledOptions,
      answer: originalCorrectOption,
    };
  });
}

function buildBalancedSequence<T extends string>(values: T[], total: number): T[] {
  if (values.length === 0) return [];
  const seed = shuffleArray(values);
  const result: T[] = [];
  for (let i = 0; i < total; i++) {
    result.push(seed[i % seed.length]);
  }
  return shuffleArray(result);
}

function buildQuestionSpecs(config: QuizConfig): QuestionSpec[] {
  const total = config.count;
  const eras = buildBalancedSequence(config.eras as Era[], total);
  const types = buildBalancedSequence(config.types as QuestionType[], total);
  const selectedDifficulties: Difficulty[] =
    config.difficulties.length > 0 ? config.difficulties : ['medium'];
  const difficulties = buildBalancedSequence<Difficulty>(selectedDifficulties, total);

  return Array.from({ length: total }, (_, index) => ({
    era: eras[index],
    type: types[index],
    difficulty: difficulties[index],
  }));
}

function countValues<T extends string>(values: T[]): Record<string, number> {
  return values.reduce<Record<string, number>>((acc, value) => {
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
}

function formatQuota(
  counts: Record<string, number>,
  labelMap?: Record<string, string>
): string {
  return Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, count]) => `${labelMap?.[key] || key}: ${count}개`)
    .join(', ');
}

function buildBatchQuotaInstruction(specs: QuestionSpec[]): string {
  const eraCounts = countValues(specs.map((s) => s.era));
  const typeCounts = countValues(specs.map((s) => s.type));
  const difficultyCounts = countValues(specs.map((s) => s.difficulty));
  const eraLabel =
    specs.some((s) => s.era.startsWith('elem') || s.era.startsWith('mid')) ? '학년/학기 분포' : '시대 분포';
  const eraDescriptions = eraLabel === '학년/학기 분포' ? MATH_TERM_DESCRIPTIONS : ERA_DESCRIPTIONS;

  return `\n[이번 배치 분포(반드시 준수)]
- ${eraLabel}: ${formatQuota(eraCounts, eraDescriptions)}
- 문제 유형 분포: ${formatQuota(typeCounts, TYPE_DESCRIPTIONS)}
- 난이도 분포: ${formatQuota(difficultyCounts, DIFFICULTY_DESCRIPTIONS)}
- 각 문항의 era/type/difficulty는 위 분포에 정확히 맞추세요.`;
}

function buildPerQuestionSpecInstruction(specs: QuestionSpec[], startQuestionNumber: number): string {
  const rows = specs
    .map((spec, index) => {
      const num = startQuestionNumber + index;
      return `- ${num}번: era=${spec.era}, type=${spec.type}, difficulty=${spec.difficulty}`;
    })
    .join('\n');
  return `\n[문항별 고정 할당표(반드시 1:1 준수)]\n${rows}`;
}

function formatYear(year: number): string {
  return year < 0 ? `기원전 ${Math.abs(year)}년` : `${year}년`;
}

function buildEraFocusInstruction(specs: QuestionSpec[], batchMeta: BatchPromptMeta): string {
  const eraCounts = countValues(specs.map((s) => s.era));
  const lines = Object.entries(eraCounts)
    .map(([era, count]) => {
      const range = ERA_FOCUS_RANGES[era as Era];
      if (!range) return `- ${era} (${count}문항): 이번 배치 중심 시기를 우선 출제`;

      if (range.kind === 'year') {
        const span = range.end - range.start + 1;
        const segment = span / batchMeta.totalBatches;
        const focusStart = Math.floor(range.start + (batchMeta.batchNumber - 1) * segment);
        const focusEnd =
          batchMeta.batchNumber === batchMeta.totalBatches
            ? range.end
            : Math.floor(range.start + batchMeta.batchNumber * segment - 1);
        const overlapPadding = Math.max(1, Math.floor(segment * 0.2));
        const allowStart = Math.max(range.start, focusStart - overlapPadding);
        const allowEnd = Math.min(range.end, focusEnd + overlapPadding);

        return `- ${era} (${count}문항): 중심 ${formatYear(focusStart)}~${formatYear(focusEnd)}, 인접 허용 ${formatYear(allowStart)}~${formatYear(allowEnd)}`;
      }

      const phaseCount = range.phases.length;
      const segment = phaseCount / batchMeta.totalBatches;
      const focusStart = Math.floor((batchMeta.batchNumber - 1) * segment);
      const focusEnd = Math.max(
        focusStart,
        Math.min(phaseCount - 1, Math.ceil(batchMeta.batchNumber * segment) - 1)
      );
      const allowStart = Math.max(0, focusStart - 1);
      const allowEnd = Math.min(phaseCount - 1, focusEnd + 1);
      const focus = range.phases.slice(focusStart, focusEnd + 1).join('/');
      const allow = range.phases.slice(allowStart, allowEnd + 1).join('/');

      return `- ${era} (${count}문항): 중심 단계 ${focus}, 인접 허용 ${allow}`;
    })
    .join('\n');

  return `\n[이번 배치 era 세부 시기 가이드]\n${lines}\n- 원칙: 중심 구간 위주로 출제하고, 인접 구간은 최대 약 20% 범위에서만 허용`;
}

function hasExpectedDistribution(questions: Question[], specs: QuestionSpec[]): boolean {
  const expectedEra = countValues(specs.map((s) => s.era));
  const expectedType = countValues(specs.map((s) => s.type));
  const expectedDifficulty = countValues(specs.map((s) => s.difficulty));

  const actualEra = countValues(questions.map((q) => q.era));
  const actualType = countValues(questions.map((q) => q.type));
  const actualDifficulty = countValues(questions.map((q) => q.difficulty));

  return (
    JSON.stringify(expectedEra) === JSON.stringify(actualEra) &&
    JSON.stringify(expectedType) === JSON.stringify(actualType) &&
    JSON.stringify(expectedDifficulty) === JSON.stringify(actualDifficulty)
  );
}

function generatePrompt(
  config: QuizConfig,
  questionCount: number = config.count,
  specs?: QuestionSpec[],
  batchMeta?: BatchPromptMeta
): string {
  if (config.subject === 'math') {
    return generateMathPrompt(config, questionCount, specs, batchMeta);
  }

  return generateHistoryPrompt(config, questionCount, specs, batchMeta);
}

function generateHistoryPrompt(
  config: QuizConfig,
  questionCount: number,
  specs?: QuestionSpec[],
  batchMeta?: BatchPromptMeta
): string {
  const eras = config.eras.map(e => ERA_DESCRIPTIONS[e]).join(', ');
  const types = config.types.map(t => TYPE_DESCRIPTIONS[t]).join(', ');
  const batchQuotaSection = specs && specs.length > 0 ? buildBatchQuotaInstruction(specs) : '';
  const perQuestionSpecSection =
    specs && specs.length > 0 && batchMeta
      ? buildPerQuestionSpecInstruction(specs, batchMeta.startQuestionNumber)
      : '';
  const eraFocusSection =
    specs && specs.length > 0 && batchMeta ? buildEraFocusInstruction(specs, batchMeta) : '';
  const batchMetaSection = batchMeta
    ? `\n[배치 메타]
- 전체 문제 수: ${batchMeta.totalCount}
- 현재 배치: ${batchMeta.batchNumber}/${batchMeta.totalBatches}
- 이번 배치 문제 수: ${questionCount}
- 이번 배치는 전체 문제의 ${batchMeta.startQuestionNumber}번부터 생성합니다.`
    : '';
  const selectedDifficultyLabels = config.difficulties
    .map((difficulty) => DIFFICULTY_DESCRIPTIONS[difficulty])
    .join(', ');
  
  return `당신은 한국 역사 전문가입니다. 다음 조건에 맞는 한국사 문제를 ${questionCount}개 생성해주세요.

[출제 조건]
- 시대: ${eras}
- 문제 유형: ${types}
- 난이도: ${selectedDifficultyLabels}
- 문제 수: ${questionCount}개
- 난이도 기준: easy=초등학교 4학년 수준, medium=초등학교 6학년 수준, hard=중학생 수준
${batchMetaSection}

[유효한 era 값] (반드시 아래 값만 사용)
- prehistoric (선사시대)
- gojoseon (고조선)
- early_states (원삼국)
- three_kingdoms (삼국시대)
- unified_silla (남북국시대)
- goryeo (고려)
- joseon_early (조선전기)
- joseon_late (조선후기)
- enlightenment (개화기)
- japanese_colonial (일제강점기)
- modern (현대)

[응답 형식 - 반드시 JSON으로만 응답]
{
  "questions": [
    {
      "id": "1",
      "type": "multiple_choice",
      "content": "문제 내용",
      "options": ["보기1", "보기2", "보기3", "보기4"],
      "answer": "정답",
      "explanation": "상세 해설 (왜 정답인지, 관련 역사적 맥락 설명)",
      "era": "joseon_early",
      "difficulty": "medium",
      "source": "출처(교과서명, 연도 등)"
    }
  ]
}

[주의사항]
1. 모든 문제는 한국사 전문가 수준의 정확한 내용이어야 합니다.
2. 해설은 학생이 이해할 수 있도록 구체적으로 작성해주세요.
3. 객관식은 반드시 4개 보기를 제공하세요.
4. 객관식 정답 위치가 (1)~(4)에 고르게 분포되도록 하세요.
5. 각 문제는 고유한 id(숫자 문자열)를 가져야 합니다.
 6. JSON 형식 외의 텍스트는 포함하지 마세요.
 7. medium 문제는 초등학교 6학년, hard 문제는 중학생이 풀 수 있는 수준으로 작성하세요.${batchQuotaSection}${eraFocusSection}${perQuestionSpecSection}`;
}

function generateMathPrompt(
  config: QuizConfig,
  questionCount: number,
  specs?: QuestionSpec[],
  batchMeta?: BatchPromptMeta
): string {
  const terms = config.eras.map((e) => MATH_TERM_DESCRIPTIONS[e]).join(', ');
  const types = config.types.map((t) => TYPE_DESCRIPTIONS[t]).join(', ');
  const selectedDifficultyLabels = config.difficulties
    .map((difficulty) => DIFFICULTY_DESCRIPTIONS[difficulty])
    .join(', ');
  const batchQuotaSection = specs && specs.length > 0 ? buildBatchQuotaInstruction(specs) : '';
  const perQuestionSpecSection =
    specs && specs.length > 0 && batchMeta
      ? buildPerQuestionSpecInstruction(specs, batchMeta.startQuestionNumber)
      : '';
  const batchMetaSection = batchMeta
    ? `\n[배치 메타]
- 전체 문제 수: ${batchMeta.totalCount}
- 현재 배치: ${batchMeta.batchNumber}/${batchMeta.totalBatches}
- 이번 배치 문제 수: ${questionCount}
- 이번 배치는 전체 문제의 ${batchMeta.startQuestionNumber}번부터 생성합니다.`
    : '';
  const unitSection =
    config.mathUnits.length > 0
      ? `\n[선택 단원(우선 출제)]\n- ${config.mathUnits.join('\n- ')}`
      : '';
  const unitRule =
    config.mathUnits.length > 0
      ? '\n6. 선택 단원에서 대부분의 문제를 출제하고, 범위를 벗어나는 문항은 최소화하세요.'
      : '';

  return `당신은 한국 수학 교육 전문가입니다. 다음 조건에 맞는 수학 문제를 ${questionCount}개 생성해주세요.

[출제 조건]
- 학년/학기: ${terms}
- 문제 유형: ${types}
- 난이도: ${selectedDifficultyLabels}
- 문제 수: ${questionCount}개
- 범위 제한: 초등학교 6학년 ~ 중학교 3학년
${batchMetaSection}
${unitSection}

[유효한 era 값] (반드시 아래 값만 사용)
- elem6_1 (초6-1)
- elem6_2 (초6-2)
- mid1_1 (중1-1)
- mid1_2 (중1-2)
- mid2_1 (중2-1)
- mid2_2 (중2-2)
- mid3_1 (중3-1)
- mid3_2 (중3-2)

[응답 형식 - 반드시 JSON으로만 응답]
{
  "questions": [
    {
      "id": "1",
      "type": "multiple_choice",
      "content": "문제 내용",
      "options": ["보기1", "보기2", "보기3", "보기4"],
      "answer": "정답",
      "explanation": "풀이 과정과 정답 근거를 단계별로 설명",
      "era": "mid1_1",
      "difficulty": "medium",
      "source": "교과 단원(예: 중1-1 문자와 식)"
    }
  ]
}

[주의사항]
1. 교과과정 범위를 벗어나지 마세요.
2. 해설은 계산/추론 과정을 포함해 학생이 따라갈 수 있게 작성하세요.
3. 객관식은 반드시 4개 보기를 제공하세요.
4. 각 문제는 고유한 id(숫자 문자열)를 가져야 합니다.
5. JSON 형식 외의 텍스트는 포함하지 마세요.${unitRule}${batchQuotaSection}${perQuestionSpecSection}`;
}

function buildSystemPrompt(subject: Subject): string {
  return subject === 'math'
    ? '당신은 한국 수학 교육 전문가입니다. 정확하고 교육적인 수학 문제를 생성합니다.'
    : '당신은 한국 역사 교육 전문가입니다. 정확하고 교육적인 역사 문제를 생성합니다.';
}

export async function generateQuestions(
  config: QuizConfig,
  llmConfig: LLMConfig
): Promise<Question[]> {
  if (llmConfig.provider === 'openai') {
    return generateWithOpenAI(config, llmConfig);
  } else {
    return generateWithGrok(config, llmConfig);
  }
}

function estimateMaxOutputTokens(questionCount: number): number {
  // 문제당 설명 포함 JSON 길이를 고려해 여유 있게 계산
  return Math.max(3500, questionCount * 450);
}

function parseQuestionsFromResponse(content: string, providerName: string): Question[] {
  const normalized = content.trim();
  const fenced = normalized.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  const candidate = fenced?.[1]?.trim() ?? normalized;
  const jsonStart = candidate.indexOf('{');
  const jsonEnd = candidate.lastIndexOf('}');
  const jsonText =
    jsonStart >= 0 && jsonEnd > jsonStart ? candidate.slice(jsonStart, jsonEnd + 1) : candidate;

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonText);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`${providerName} returned invalid JSON: ${message}`);
  }

  if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as { questions?: unknown }).questions)) {
    throw new Error(`${providerName} response does not contain a valid questions array`);
  }

  return randomizeMultipleChoiceOptions((parsed as { questions: Question[] }).questions);
}

function withSequentialIds(questions: Question[], start: number = 1): Question[] {
  return questions.map((q, idx) => ({
    ...q,
    id: String(start + idx),
  }));
}

async function generateWithOpenAI(
  quizConfig: QuizConfig,
  config: LLMConfig
): Promise<Question[]> {
  const specs = buildQuestionSpecs(quizConfig);
  const openai = new OpenAI({
    apiKey: config.apiKey,
    dangerouslyAllowBrowser: true,
  });
  const model = config.model || 'gpt-5.2';
  const totalCount = quizConfig.count;
  const batchSize = 12;
  const questions: Question[] = [];

  for (let offset = 0; offset < totalCount; offset += batchSize) {
    const currentCount = Math.min(batchSize, totalCount - offset);
    const batchSpecs = specs.slice(offset, offset + currentCount);
    const batchNumber = Math.floor(offset / batchSize) + 1;
    const totalBatches = Math.ceil(totalCount / batchSize);
    const prompt = generatePrompt(quizConfig, currentCount, batchSpecs, {
      batchNumber,
      totalBatches,
      totalCount,
      startQuestionNumber: offset + 1,
    });
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await openai.responses.create({
          model,
          input: [
            {
              role: 'system',
              content: buildSystemPrompt(quizConfig.subject),
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          text: {
            format: {
              type: 'json_object',
            },
          },
          max_output_tokens: estimateMaxOutputTokens(currentCount),
          temperature: attempt === 1 ? 0.7 : 0.2,
        });

        const content = response.output_text;
        if (!content) throw new Error('No response from OpenAI');

        const parsedQuestions = parseQuestionsFromResponse(content, 'OpenAI');
        if (parsedQuestions.length < currentCount) {
          throw new Error(
            `OpenAI returned too few questions for a batch: expected ${currentCount}, got ${parsedQuestions.length}`
          );
        }

        const batchQuestions = parsedQuestions.slice(0, currentCount);
        if (!hasExpectedDistribution(batchQuestions, batchSpecs)) {
          throw new Error('OpenAI batch distribution mismatch (era/type/difficulty)');
        }

        questions.push(...batchQuestions);
        lastError = null;
        break;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }

    if (lastError) {
      throw lastError;
    }
  }

  return withSequentialIds(questions);
}

async function generateWithGrok(
  quizConfig: QuizConfig,
  config: LLMConfig
): Promise<Question[]> {
  // Grok은 OpenAI 호환 API를 제공
  const grok = new OpenAI({
    apiKey: config.apiKey,
    baseURL: 'https://api.x.ai/v1',
    dangerouslyAllowBrowser: true,
  });

  const model = config.model || 'grok-4.1-fast-non-reasoning';
  const totalCount = quizConfig.count;
  const batchSize = 12;
  const specs = buildQuestionSpecs(quizConfig);
  const questions: Question[] = [];

  for (let offset = 0; offset < totalCount; offset += batchSize) {
    const currentCount = Math.min(batchSize, totalCount - offset);
    const batchSpecs = specs.slice(offset, offset + currentCount);
    const batchNumber = Math.floor(offset / batchSize) + 1;
    const totalBatches = Math.ceil(totalCount / batchSize);
    const prompt = generatePrompt(quizConfig, currentCount, batchSpecs, {
      batchNumber,
      totalBatches,
      totalCount,
      startQuestionNumber: offset + 1,
    });
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await grok.responses.create({
          model,
          input: [
            {
              role: 'system',
              content: buildSystemPrompt(quizConfig.subject),
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          text: {
            format: {
              type: 'json_object',
            },
          },
          max_output_tokens: estimateMaxOutputTokens(currentCount),
          temperature: attempt === 1 ? 0.7 : 0.2,
        });

        const content = response.output_text;
        if (!content) throw new Error('No response from Grok');
        const parsedQuestions = parseQuestionsFromResponse(content, 'Grok');
        if (parsedQuestions.length < currentCount) {
          throw new Error(
            `Grok returned too few questions for a batch: expected ${currentCount}, got ${parsedQuestions.length}`
          );
        }
        const batchQuestions = parsedQuestions.slice(0, currentCount);
        if (!hasExpectedDistribution(batchQuestions, batchSpecs)) {
          throw new Error('Grok batch distribution mismatch (era/type/difficulty)');
        }

        questions.push(...batchQuestions);
        lastError = null;
        break;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }

    if (lastError) {
      throw lastError;
    }
  }

  // 총 개수를 정확히 맞추고 ID를 다시 정렬
  return withSequentialIds(questions);
}
